import { gql, useQuery } from "@apollo/client";
import React, { useEffect, useState, useMemo } from "react";
import { useFormContext, Controller } from "react-hook-form";

import {
  LIST_DISCIPLINES,
  LIST_DISCIPLINES_QUERY_OPTIONS,
} from "apollo/queries/disciplines";
import SingleSelectField from "components/ui/single-select";
import { formatListArray, seatSizes } from "utils";
import { useHorseStore } from "stores/horse/useHorseStore";
import { useAuthStore } from "stores/auth/authStore";

const Step3SaddleSpecifications = () => {
  const user = useAuthStore((state) => state.user);
  const horseData = useHorseStore((state) => state.horseData);

  const { control, setValue, trigger, watch } = useFormContext();

  const watchedSeatSize = watch("seatSize");
  const watchedDiscipline = watch("discipline");
  const { data: listDisciplinesData } = useQuery(
    gql(LIST_DISCIPLINES),
    LIST_DISCIPLINES_QUERY_OPTIONS
  );
  const [westernDisciplines, setWesternDisciplines] = useState([]);

  const userSizeArray = useMemo(() => {
    if (!user?.size) return [];
    return Array.isArray(user.size) ? user.size : [user.size];
  }, [user?.size]);

  const isSeatSizeMultiple = userSizeArray.length > 1;
  const singleSeatSize = userSizeArray.length === 1 ? userSizeArray[0] : null;

  const filteredSeatSizes = useMemo(() => {
    if (userSizeArray.length === 0) return seatSizes;
    return seatSizes.filter((s) =>
      userSizeArray.some((val) => val.toString() === s.value.toString())
    );
  }, [userSizeArray]);

  const horseDisciplineArray = useMemo(() => {
    if (!horseData?.discipline) return [];
    return Array.isArray(horseData.discipline)
      ? horseData.discipline
      : [horseData.discipline];
  }, [horseData?.discipline]);

  const isDisciplineMultiple = horseDisciplineArray.length > 1;
  const singleDiscipline = horseDisciplineArray.length === 1 ? horseDisciplineArray[0] : null;

  useEffect(() => {
    if (listDisciplinesData?.listDisciplines) {
      const formattedOptions = formatListArray(
        listDisciplinesData.listDisciplines
      );
      const filteredWesternOptions = formattedOptions.filter(
        (option) => String(option?.label ?? "").toUpperCase() === "WESTERN"
      );
      if (filteredWesternOptions.length > 0) {
        const flatOptions = filteredWesternOptions[0].options || [];
        setWesternDisciplines(flatOptions);
      }
    }
  }, [listDisciplinesData]);

  const filteredDisciplineOptions = useMemo(() => {
    if (horseDisciplineArray.length === 0) return westernDisciplines;
    return westernDisciplines.filter((opt) =>
      horseDisciplineArray.includes(opt.value)
    );
  }, [horseDisciplineArray, westernDisciplines]);

  useEffect(() => {
    if (!isSeatSizeMultiple && singleSeatSize) {
      setValue("seatSize", Number(singleSeatSize) || singleSeatSize, { shouldValidate: true });
    }
  }, [isSeatSizeMultiple, singleSeatSize, setValue]);

  useEffect(() => {
    if (!isDisciplineMultiple && singleDiscipline) {
      setValue("discipline", singleDiscipline, { shouldValidate: true });
    }
  }, [isDisciplineMultiple, singleDiscipline, setValue]);

  const getSeatSizeLabel = (val) => {
    const found = seatSizes.find((s) => s.value.toString() === val?.toString());
    return found ? found.label : val;
  };

  const getDisciplineLabel = (val) => {
    const found = westernDisciplines.find((d) => d.value === val);
    return found ? found.label : val?.replace(/_/g, " ");
  };

  const bothSingle = !isSeatSizeMultiple && !isDisciplineMultiple;

  if (bothSingle) {
    return (
      <div className="w-full">
        <h3 className="text-base sm:text-lg lg:text-xl font-bold text-[#2b364b] mb-4 sm:mb-6 text-center font-montserrat">
          Saddle Specifications
        </h3>

        <div className="max-w-md mx-auto">
          <div className="border border-[#e0e0e0] rounded-2xl p-5 sm:p-6 bg-[#fafbfc]">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 rounded-full bg-[#2b364b] flex items-center justify-center flex-shrink-0">
                <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-[#2b364b] font-montserrat">
                Specifications Auto-Filled
              </p>
            </div>

            <p className="text-xs text-gray-500 font-montserrat mb-5">
              Based on your profile and horse data, we&apos;ve selected the following specifications for your custom saddle.
            </p>

            <div className="space-y-3">
              <div className="flex items-center justify-between py-2.5 px-4 bg-white rounded-full border border-[#e8e8e8]">
                <span className="text-xs text-gray-500 font-montserrat">Seat Size</span>
                <span className="text-sm font-semibold text-[#2b364b] font-montserrat">
                  {singleSeatSize ? `${getSeatSizeLabel(singleSeatSize)}"` : "—"}
                </span>
              </div>
              <div className="flex items-center justify-between py-2.5 px-4 bg-white rounded-full border border-[#e8e8e8]">
                <span className="text-xs text-gray-500 font-montserrat">Discipline</span>
                <span className="text-sm font-semibold text-[#2b364b] font-montserrat capitalize">
                  {singleDiscipline ? getDisciplineLabel(singleDiscipline) : "—"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <h3 className="text-base sm:text-lg lg:text-xl font-bold text-[#2b364b] mb-4 sm:mb-6 text-center font-montserrat">
        Saddle Specifications
      </h3>

      <div className="max-w-md mx-auto space-y-4">
        {/* Seat Size */}
        {isSeatSizeMultiple ? (
          <Controller
            name="seatSize"
            control={control}
            rules={{ required: "Please select a seat size" }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <SingleSelectField
                  label="Seat Size"
                  options={filteredSeatSizes}
                  placeholder="Select seat size"
                  className="w-full"
                  value={field.value}
                  onChange={(option) => {
                    field.onChange(option?.value || null);
                    trigger("seatSize");
                  }}
                  stylesOverride={{
                    control: (base) => ({
                      ...base,
                      borderColor: error ? "#ef4444" : "#999",
                      boxShadow: "none",
                      borderRadius: "9999px",
                      height: "51.8px",
                      cursor: "pointer",
                      "&:hover": { borderColor: error ? "#ef4444" : "#999" },
                      "&:focus-within": { borderColor: error ? "#ef4444" : "#999" },
                    }),
                    dropdownIndicator: (base) => ({ ...base, marginBottom: "10px !important" }),
                    clearIndicator: (base) => ({ ...base, marginBottom: "10px !important" }),
                    placeholder: (base) => ({ ...base, paddingTop: "15px !important" }),
                  }}
                />
                {!watchedSeatSize && (
                  <p className="text-gray-500 text-xs mt-1.5 ml-4 font-montserrat">
                    Multiple sizes found — please select one.
                  </p>
                )}
                {error && (
                  <p className="text-red-500 text-xs sm:text-sm mt-1 ml-4 font-montserrat">{error.message}</p>
                )}
              </div>
            )}
          />
        ) : (
          <div className="flex items-center gap-3 py-2.5 px-4 bg-[#fafbfc] rounded-full border border-[#e0e0e0]">
            <div className="w-5 h-5 rounded-full bg-[#2b364b] flex items-center justify-center flex-shrink-0">
              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <span className="text-xs text-gray-500 font-montserrat">Seat Size</span>
            <span className="text-sm font-semibold text-[#2b364b] font-montserrat ml-auto">
              {singleSeatSize ? `${getSeatSizeLabel(singleSeatSize)}"` : "—"}
            </span>
          </div>
        )}

        {/* Discipline */}
        {isDisciplineMultiple ? (
          <Controller
            name="discipline"
            control={control}
            rules={{ required: "Please select a discipline" }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <SingleSelectField
                  label="Discipline"
                  options={filteredDisciplineOptions}
                  placeholder="Select discipline"
                  className="w-full"
                  value={field.value}
                  onChange={(option) => {
                    field.onChange(option?.value || null);
                    trigger("discipline");
                  }}
                  stylesOverride={{
                    control: (base) => ({
                      ...base,
                      borderColor: error ? "#ef4444" : "#999",
                      boxShadow: "none",
                      borderRadius: "9999px",
                      height: "51.8px",
                      cursor: "pointer",
                      "&:hover": { borderColor: error ? "#ef4444" : "#999" },
                      "&:focus-within": { borderColor: error ? "#ef4444" : "#999" },
                    }),
                    dropdownIndicator: (base) => ({ ...base, marginBottom: "10px !important" }),
                    clearIndicator: (base) => ({ ...base, marginBottom: "10px !important" }),
                    placeholder: (base) => ({ ...base, paddingTop: "15px !important" }),
                  }}
                />
                {!watchedDiscipline && !error && (
                  <p className="text-gray-500 text-xs mt-1.5 ml-4 font-montserrat">
                    Multiple disciplines found — please select one.
                  </p>
                )}
                {error && (
                  <p className="text-red-500 text-xs sm:text-sm mt-1 ml-4 font-montserrat">{error.message}</p>
                )}
              </div>
            )}
          />
        ) : (
          <div className="flex items-center gap-3 py-2.5 px-4 bg-[#fafbfc] rounded-full border border-[#e0e0e0]">
            <div className="w-5 h-5 rounded-full bg-[#2b364b] flex items-center justify-center flex-shrink-0">
              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <span className="text-xs text-gray-500 font-montserrat">Discipline</span>
            <span className="text-sm font-semibold text-[#2b364b] font-montserrat ml-auto capitalize">
              {singleDiscipline ? getDisciplineLabel(singleDiscipline) : "—"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Step3SaddleSpecifications;
